import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createWarrantyClaim,
  getWarrantyClaim,
  getWarrantyEligibility,
  listWarrantyClaims,
  markClaimViewed,
  respondToClaim,
} from './api';
import type {
  CreateClaimInput,
  RespondToClaimInput,
  WarrantyClaim,
  WarrantyClaimDetail,
} from './types';

export const warrantyKeys = {
  all: ['warranty'] as const,
  list: () => [...warrantyKeys.all, 'list'] as const,
  detail: (id: string) => [...warrantyKeys.all, 'detail', id] as const,
  eligibility: (deviceId: string) => [...warrantyKeys.all, 'eligibility', deviceId] as const,
};

/** Eligibility of a device to raise a warranty claim (gates the detail-screen action). */
export function useWarrantyEligibility(deviceId: string, enabled = true) {
  return useQuery({
    queryKey: warrantyKeys.eligibility(deviceId),
    queryFn: () => getWarrantyEligibility(deviceId),
    enabled: enabled && !!deviceId,
    staleTime: 0,
  });
}

/** The grower's full warranty-claim list (story 101872 — listing & history). */
export function useWarrantyClaims() {
  return useQuery({ queryKey: warrantyKeys.list(), queryFn: listWarrantyClaims });
}

/** Full claim detail for the detail drawer (story 101872). */
export function useWarrantyClaim(id: string, enabled = true) {
  return useQuery({
    queryKey: warrantyKeys.detail(id),
    queryFn: () => getWarrantyClaim(id),
    enabled: enabled && !!id,
  });
}

/** Submit a claim; on success the list + the device's eligibility are invalidated. */
export function useCreateWarrantyClaim(deviceId?: string) {
  const qc = useQueryClient();
  return useMutation<WarrantyClaim, unknown, CreateClaimInput>({
    mutationFn: (input) => createWarrantyClaim(input),
    onSuccess: (_claim, input) => {
      void qc.invalidateQueries({ queryKey: warrantyKeys.list() });
      void qc.invalidateQueries({
        queryKey: warrantyKeys.eligibility(deviceId ?? input.deviceId),
      });
    },
  });
}

/** Reply to an Info Request; refreshes the detail + list (status returns to Under Review). */
export function useRespondToClaim(id: string) {
  const qc = useQueryClient();
  return useMutation<WarrantyClaimDetail, unknown, RespondToClaimInput>({
    mutationFn: (input) => respondToClaim(id, input),
    onSuccess: (detail) => {
      qc.setQueryData(warrantyKeys.detail(id), detail);
      void qc.invalidateQueries({ queryKey: warrantyKeys.list() });
    },
  });
}

/** Clear a claim's unread / response-required indicators once the grower has seen it. */
export function useMarkClaimViewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markClaimViewed(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: warrantyKeys.list() });
    },
  });
}
